/**
 * MARS Portfolio dashboard — top-level page.
 *
 * Uses a centered page title with the per-fund tab toggle docked to the
 * right of the title row (not a left-aligned title with underline tabs).
 * The heading row is inlined here rather than going through `<PageHeader>`
 * to keep that centered-title + right-button-group layout.
 *
 * Layout map:
 *
 *   [Title centered] [Asset Positions / Risk Metrics / Performance →]
 *   [As of <date> centered]
 *   ┌── white surface card ──────────────────────────────────────┐
 *   │ <ExposuresSection> + <AssetToggles> + per-tab body         │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The Fund dropdown is a quiet inline control at the top-right of the
 * surface card, shown only to superusers so they can switch funds.
 */
import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import ExposuresSection from './components/ExposuresSection';
import PerformanceSection from './components/PerformanceSection';
import RiskMetricsUnified from './components/RiskMetricsUnified';
import { fetchFunds, marsPortfolioKeys } from './api';
import { useMarsPortfolioStore, type DashboardTab } from './store';
import { trackActivity } from '@/lib/analytics/tracker';
import { formatLong } from '@/lib/utils/dates';
import type { Fund, FundId } from './types';

/** Tab value → human-readable name for `page_view` descriptions. */
const TAB_LABELS: Record<DashboardTab, string> = {
  'asset-positions': 'Asset Positions',
  'risk-metrics': 'Risk Metrics',
  performance: 'Performance',
};

interface DashboardTabDef {
  value: DashboardTab;
  label: string;
}

const DASHBOARD_TABS: ReadonlyArray<DashboardTabDef> = [
  { value: 'asset-positions', label: 'Asset Positions' },
  { value: 'risk-metrics', label: 'Risk Metrics' },
  { value: 'performance', label: 'Performance' },
];

/**
 * Right-docked button group used in place of the shared `<Tabs>` primitive.
 * Active tab is teal-filled, inactive tabs are transparent with hover state.
 */
function TabButtonGroup({
  value,
  onChange,
}: {
  value: DashboardTab;
  onChange: (next: DashboardTab) => void;
}): JSX.Element {
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-1">
      {DASHBOARD_TABS.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            className={
              'px-[20px] py-2 rounded-[4px] text-[0.9rem] font-medium transition-colors focus:outline-none ' +
              (active
                ? 'bg-[color:var(--accent)] text-white shadow-sm'
                : 'bg-transparent text-gray-700 hover:bg-gray-200')
            }
            aria-pressed={active}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export default function MarsPortfolioPage(): JSX.Element {
  const fundId = useMarsPortfolioStore((s) => s.fund);
  const setFund = useMarsPortfolioStore((s) => s.setFund);
  const tab = useMarsPortfolioStore((s) => s.tab);
  const setTab = useMarsPortfolioStore((s) => s.setTab);
  const [searchParams] = useSearchParams();

  // The store defaults to mars-fqm; without this sync, navigating to
  // ?fund=mars-dar-nn or ?fund=mars-fidelity-* leaves the store on
  // mars-fqm and the page silently renders FQM data (or fails when the
  // user lacks FQM access). Mirror the URL into the store on mount and
  // whenever the query string changes.
  const urlFund = searchParams.get('fund');
  useEffect(() => {
    if (urlFund && urlFund !== fundId) {
      setFund(urlFund as FundId);
    }
  }, [urlFund, fundId, setFund]);

  const fundsQuery = useQuery({
    queryKey: marsPortfolioKeys.funds(),
    queryFn: fetchFunds,
    staleTime: 5 * 60_000,
  });

  const fund: Fund | undefined = fundsQuery.data?.funds.find((f) => f.id === fundId);
  const availableFundIds = fundsQuery.data?.funds.map((f) => f.id) ?? [];
  const isLoadingFund = fundsQuery.isLoading;

  const title = fund ? fund.display_name : 'MARS Portfolio Dashboard';
  const fundName = fund?.display_name ?? fundId;

  // Tab switches are tracked as synthetic page_views (matches the legacy
  // mars-portfolio-dashboard.js tracking).
  const selectTab = useCallback(
    (next: DashboardTab): void => {
      setTab(next);
      trackActivity('page_view', `Portfolio: ${fundName} - ${TAB_LABELS[next]}`, {});
    },
    [setTab, fundName],
  );

  return (
    <div>
      {/* Title centered, view-toggle button group docked absolute-right. */}
      <section className="mx-auto max-w-[1200px] px-4 pt-6 pb-4">
        <div className="relative mb-2 flex flex-wrap items-center justify-center gap-3">
          <h2
            className="font-[var(--font-heading)] text-[1.75rem] font-bold text-[color:var(--text-dark)] m-0 text-center"
            style={{ lineHeight: '42px' }}
          >
            {title}
          </h2>
          <div className="md:absolute md:right-0 md:top-1/2 md:-translate-y-1/2">
            <TabButtonGroup value={tab} onChange={selectTab} />
          </div>
        </div>
        <p className="font-[var(--font-heading)] text-[0.9rem] text-[color:var(--gray-500)] m-0 text-center mb-6">
          As of <span>{formatLong(new Date())}</span>
        </p>
      </section>

      <div className="mx-auto max-w-[1200px] px-4 pb-8">
        {isLoadingFund ? (
          <div className="p-8 text-[color:var(--text-dark)] opacity-60">
            Loading fund configuration…
          </div>
        ) : !fund ? (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-6 text-sm text-amber-900">
            <p className="m-0 font-semibold">
              Fund <code className="bg-amber-100 px-1 rounded">{fundId}</code> is not available for
              your account.
            </p>
            {availableFundIds.length > 0 ? (
              <p className="mt-2 mb-0">
                Funds you can view:{' '}
                {availableFundIds.map((id, i) => (
                  <span key={id}>
                    {i > 0 && ', '}
                    <a
                      href={`/mars-portfolio-dashboard?fund=${id}`}
                      className="underline text-amber-800 hover:text-amber-950"
                    >
                      {id}
                    </a>
                  </span>
                ))}
              </p>
            ) : (
              <p className="mt-2 mb-0 italic">
                No MARS portfolio funds are available — contact an administrator to grant access.
              </p>
            )}
          </div>
        ) : (
          // White surface card. The Fund dropdown is not shown to
          // non-superusers (fund switching happens via the nav dropdown).
          <div className="bg-white rounded-xl overflow-hidden shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)]">
            {/* Card uses p-0; each section applies its own px-4. */}
            <div className="p-0">
              {tab === 'asset-positions' ? <ExposuresSection fund={fund} /> : null}
              {tab === 'risk-metrics' ? <RiskMetricsUnified fund={fund} /> : null}
              {tab === 'performance' ? <PerformanceSection fund={fund} /> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
